import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { ShipService } from '../../../services/resources/ship.service';
import { ShipInterface } from '../../../core/models';
import { SimpleCheckModalComponent } from '../../../common/modals/simple-check-modal.component';
import { TimestampToDatePipe } from '../../../core/timestamp-to-date.pipe';

@Component({
  selector: 'app-ship-dashboard',
  imports: [
    CommonModule,
    RouterModule,
    SimpleCheckModalComponent,
    TimestampToDatePipe,
  ],
  standalone: true,
  template: `
    <div class="mx-auto max-w-7xl pt-6 px-4 sm:px-6 lg:px-8">
      <div class="px-4 sm:px-6 lg:px-8">
        <div class="sm:flex sm:items-center pt-6">
          <div class="sm:flex-auto">
            <h1 class="text-base font-semibold leading-6 text-gray-900">
              Ships
            </h1>
            <p class="mt-2 text-sm text-gray-700">
              A list of all the Ships for booking.
            </p>
          </div>
          <div class="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
            <button
              type="button"
              routerLink="/ships/new"
              class="justify-center disabled:opacity-25 rounded-md bg-columbus-blue-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-columbus-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-columbus-blue-600"
            >
              Create new Ship
            </button>
          </div>
        </div>
        <div class="mt-8 flow-root">
          <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div
              class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8"
              *ngIf="ships$ | async as ships"
            >
              <table class="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th
                      scope="col"
                      class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0"
                    >
                      ID - Identifier
                    </th>
                    <th
                      scope="col"
                      class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0"
                    >
                      Company Name
                    </th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Ship Name
                    </th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Ship Tel.
                    </th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Created
                    </th>
                    <th
                      scope="col"
                      class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Edited
                    </th>
                    <th scope="col" class="relative py-3.5 pl-3 pr-4 sm:pr-0">
                      <span class="sr-only">Edit</span>
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200">
                  <tr *ngFor="let ship of ships">
                    <td
                      class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-light text-gray-600 sm:pl-0"
                    >
                      {{ ship.id }}
                    </td>
                    <td
                      class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0"
                    >
                      <span class="block max-w-[15ch] truncate">
                        {{ ship.companyName }}
                      </span>
                    </td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >
                      {{ ship.shipName }}
                    </td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >
                      {{ ship.receptionTel }}
                    </td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >
                      {{
                        ship.createdDate | timestampToDate | date: 'dd.MM.YYYY'
                      }}
                    </td>
                    <td
                      class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                    >
                      {{
                        ship.lastEditDate | timestampToDate | date: 'dd.MM.YYYY'
                      }}
                    </td>
                    <td
                      class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0"
                    >
                      <div class="flex flex-col justify-center items-center">
                        <div
                          routerLink="/ships/{{ ship.id }}"
                          class="text-columbus-blue-600 hover:text-columbus-blue-900 hover:cursor-pointer"
                        >
                          Edit<span class="sr-only">Edit</span>
                        </div>
                        <div
                          (click)="onDelete(ship)"
                          class="text-red-600 hover:text-red-900 hover:cursor-pointer"
                        >
                          Delete<span class="sr-only">Delete</span>
                        </div>
                      </div>
                    </td>
                  </tr>

                  <!-- More people... -->
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div *ngIf="onDeleteToggle && selectedShip">
      <app-simple-check-modal
        [title]="
          'Delete Ship' + selectedShip.companyName + ' ' + selectedShip.shipName
        "
        [message]="'Do you want to delete the ship?'"
        [positiveButtonText]="'Yes'"
        [negativeButtonText]="'No'"
        (close)="onCloseDeleteModal($event)"
      ></app-simple-check-modal>
    </div>
  `,
  styles: ``,
})
export class ShipDashboardComponent {
  shipServices = inject(ShipService);
  ships$ = this.shipServices.getShips();

  onDeleteToggle = false;
  selectedShip: ShipInterface | undefined = undefined;

  constructor() {}
  ngOnInit(): void {}

  toggleDeleteModal() {
    this.onDeleteToggle = !this.onDeleteToggle;
  }

  onDelete(ship: ShipInterface) {
    this.selectedShip = ship;
    this.toggleDeleteModal();
  }

  onCloseDeleteModal(value: boolean) {
    if (value && this.selectedShip) {
      this.shipServices.deleteShip(this.selectedShip.id);
    }

    this.toggleDeleteModal();
  }
}
